import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { readSaved, save } from '../gameDeck';
import { PENDING_TV, displayKey, displayRequest, ensureDisplay, hostedRooms, publicDisplay } from '../creativeDisplay';

export default function CreativeConnect() {
  const [params] = useSearchParams(), navigate = useNavigate();
  const [rooms] = useState(hostedRooms);
  const [room, setRoom] = useState(rooms.some(r => r.code === params.get('room')) ? params.get('room') : rooms[0]?.code || '');
  const [code, setCode] = useState(params.get('code') || readSaved(PENDING_TV, '') || '');
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const pending = useRef(null);
  useEffect(() => () => pending.current?.abort(), []);
  const selected = rooms.find(r => r.code === room);
  const paired = readSaved(displayKey(room), null)?.paired;
  async function connect(e) {
    e.preventDefault(); if (busy || !selected) return;
    setBusy('connect'); setError('');
    const controller = new AbortController(); pending.current = controller;
    try {
      const channel = await ensureDisplay(room, controller.signal);
      if (controller.signal.aborted) return;
      // Refresh the projection before attaching a new viewer. An old channel
      // must not flash its previous card while the host is privately writing.
      const revision = Math.max(Date.now(), (channel.revision || 0) + 1);
      await displayRequest('publish', { id: channel.id, token: channel.token, revision, snapshot: publicDisplay(room, selected.route, readSaved(room + '_data', {})) }, controller.signal);
      if (controller.signal.aborted) return;
      channel.revision = revision;
      await displayRequest('approve', { id: channel.id, token: channel.token, code }, controller.signal);
      if (controller.signal.aborted) return;
      save(displayKey(room), { ...channel, paired: true }); save(PENDING_TV, '');
      navigate(`/creative-mode/${room}/${selected.route}`, { replace: true });
    } catch (e) { if (!controller.signal.aborted) setError(e.message); } finally { if (!controller.signal.aborted) setBusy(false); }
  }
  async function disconnect() {
    setBusy('disconnect'); setError('');
    const controller = new AbortController(); pending.current = controller;
    try {
      const channel = readSaved(displayKey(room), null);
      await displayRequest('disconnect', { id: channel.id, token: channel.token }, controller.signal);
      if (controller.signal.aborted) return;
      save(displayKey(room), { ...channel, paired: false });
      navigate(`/creative-mode/${room}/${selected.route}`, { replace: true });
    } catch (e) { if (!controller.signal.aborted) setError(e.message); } finally { if (!controller.signal.aborted) setBusy(false); }
  }
  return <section className="creative-form creative-connect">
    <span className="eyebrow">PHONE CONTROLS. TV PLAYS.</span><h1>{code ? 'Your room. Bigger screen.' : 'Connect a TV.'}</h1>
    {rooms.length ? <>
      <p>{code ? 'Your prompts stay on your phone. Only the lobby and current card appear on the TV.' : 'On your TV, open drinkifyouthink.com and choose “Play on a TV”. Scan its QR code with this phone, or enter the TV code below.'}</p>
      <form onSubmit={connect}>
        {rooms.length > 1 ? <><label htmlFor="display-room">Your room</label><select id="display-room" value={room} onChange={e => setRoom(e.target.value)}>{rooms.map(r => <option key={r.code} value={r.code}>Room {r.code}</option>)}</select></> : <p className="display-room-label">Room {selected?.code}</p>}
        <label htmlFor="tv-code">TV code</label><input id="tv-code" autoComplete="off" autoCapitalize="characters" placeholder="ABCD EFGH" maxLength={9} value={code} onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 8))} />
        <button className="button dark full" disabled={!!busy || code.replace(/\s/g, '').length !== 8}>{busy === 'connect' ? 'Connecting…' : 'Connect this room'}</button>
      </form>
      {paired && <button className="text-link" disabled={!!busy} onClick={disconnect}>{busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect TV'}</button>}
      <Link className="text-link" to={`/creative-mode/${room}/${selected?.route || 'enter-prompts'}`}>Back to room</Link>
    </> : <><p>Open this link in the browser where you created your room. Or start a new room on this phone.</p><Link className="button dark full" to="/#creative" onClick={() => save(PENDING_TV, code)}>Create a room</Link></>}
    {error && <p role="alert" className="error-message">{error}</p>}
  </section>;
}
